/**
 * src/engine/borrowVsSell.ts — the "sell" counterfactual (§6.14).
 *
 * Funds the SAME net spending stream (the per-month draws) by selling assets instead of
 * borrowing. Each month the portfolio evolves identically (growth/dividends/contributions/
 * shocks), then enough is sold to net the draw — grossing up for capital-gains tax. The
 * visible asymmetry vs. the borrow path is the core arithmetic of buy-borrow-die: funding
 * `d` by selling requires S > d because of tax.
 */
import type { ScenarioInputs, SellPathResult } from '@/types';
import { addMonths, startOfMonth } from 'date-fns';
import { type BucketState, liquidationOrder } from './collateral';
import { advancePortfolio } from './portfolio';
import { gainFraction } from './tax';
import { type GainsTaxModel, buildGainsTaxModel } from './gainsTax';
import { buildDrawArray, buildInitialStates, scheduleToArray } from './projection';

export interface SellSale {
  grossSale: number;
  realizedTax: number;
  denomInvalid: boolean;
}

/**
 * Sell from buckets in liquidation order to net `net` after tax (§6.14, §6.22). Each
 * bucket realizes its own gain fraction. Mutates `states`.
 */
export function sellToNet(
  states: BucketState[],
  net: number,
  model: GainsTaxModel,
  policy: ScenarioInputs['liquidationPolicy'],
): SellSale {
  let remaining = net;
  let grossSale = 0;
  let realizedTax = 0;
  let denomInvalid = false;
  if (net <= 0) return { grossSale: 0, realizedTax: 0, denomInvalid: false };

  for (const i of liquidationOrder(states, policy)) {
    if (remaining <= 0) break;
    const b = states[i]!;
    if (b.value <= 0) continue;
    const g = gainFraction(b.basis, b.value);
    // The model owns the gross-up: flat multiplies by one rate, bracketed walks the
    // 0/15/20 + NIIT boundaries the sale itself crosses (§6.12b).
    const sale = model.grossUpToNet(remaining, g, b.value);
    if (sale.denomInvalid) denomInvalid = true;
    const s = Math.min(sale.grossSale, b.value);
    model.realize(sale.gain);
    grossSale += s;
    realizedTax += sale.tax;
    b.value -= s;
    b.basis = Math.max(0, b.basis - s * (1 - g));
    remaining -= s - sale.tax;
  }
  return { grossSale, realizedTax, denomInvalid };
}

export interface SellPathFull extends SellPathResult {
  finalStates: BucketState[];
}

export interface SellPathOptions {
  /** Start month, so calendar years (and therefore tax years) are real. */
  startDate?: Date;
}

/** Run the parallel sell-path simulation funding the same draws as the borrow path. */
export function runSellPath(inputs: ScenarioInputs, opts: SellPathOptions = {}): SellPathFull {
  const months = inputs.years * 12;
  const startMonth = startOfMonth(opts.startDate ?? new Date());
  const states = buildInitialStates(inputs);
  // The sell path is its own world: it gets its OWN tax-year position, never shared with
  // the borrow path's forced-liquidation model.
  const model = buildGainsTaxModel(inputs, startMonth.getFullYear());
  const drawArr = buildDrawArray(inputs, months);
  const contributionArr = scheduleToArray(inputs.contributions, months);
  const shockArr = scheduleToArray(inputs.collateralShocks, months, 'pctChange');

  const rows: SellPathResult['rows'] = [];
  let cumulativeTax = 0;

  for (let m = 0; m < months; m++) {
    model.setYear(addMonths(startMonth, m).getFullYear());
    advancePortfolio(states, {
      contributionThisMonth: contributionArr[m]!,
      shockPctThisMonth: shockArr[m]!,
      dividendHandling: inputs.dividendHandling,
    });
    const sale = sellToNet(states, drawArr[m]!, model, inputs.liquidationPolicy);
    cumulativeTax += sale.realizedTax;
    const portfolio = states.reduce((sum, b) => sum + b.value, 0);
    rows.push({ monthIndex: m, portfolio, netWorth: portfolio, cumulativeTax, grossSale: sale.grossSale });
  }

  // months ≥ 12, so there is always a last row.
  return {
    rows,
    endingNetWorth: rows[rows.length - 1]!.netWorth,
    cumulativeTax,
    finalStates: states,
  };
}
